import React, { useState, useEffect } from 'react';
import useApi from '../../../utils/api-manager/Helper/useApi';
import { BarChart } from '../../common/ReusableComponent/Chart';
import { ToastContainer } from 'react-toastify';
import { Camera, Activity, AlertTriangle, Image, Video, HardDrive, Eye, ShieldAlert, Tag, Bell, Film } from 'lucide-react';
import loader from '../../../assets/Image/Loader2.gif';

const card = {
  background: 'white',
  borderRadius: '12px',
  padding: '20px',
  boxShadow: '0 2px 6px rgba(0,0,0,0.07)',
  border: '1px solid #e5e7eb',
};

const SEVERITY_COLOR = { low: '#10b981', medium: '#f59e0b', high: '#ef4444' , critical: '#8b5cf6'};

const NoData = () => (
  <div style={{ textAlign: 'center', padding: '24px', color: '#94a3b8', fontSize: '13px', fontWeight: '500' }}>
    No data available
  </div>
);

export default function MonitorDashboard() {
  const { Get, getHost } = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    setLoading(true); setError(false);
    try { setData(await Get('monitorDashboard')); }
    catch { setError(true); }
    finally { setLoading(false); }
  };

  if (loading) {
    return (
      <div className="responsive-box">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <img src={loader} alt="Loading..." style={{ width: '80px' }} />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="responsive-box">
        <div style={{ textAlign: 'center', padding: '80px', background: '#f8fafc', borderRadius: '16px' }}>
          {/* <div style={{ fontSize: '40px', marginBottom: '12px' }}>⚠️</div> */}
          <div style={{ fontSize: '16px', fontWeight: '600', color: '#334155', marginBottom: '16px' }}>No data available</div>
          {/* <button onClick={fetchDashboard} style={{ background: '#FFAF32', border: 'none', borderRadius: '10px', padding: '10px 24px', fontWeight: '700', cursor: 'pointer', fontSize: '14px' }}>
            ↺ Retry
          </button> */}
        </div>
      </div>
    );
  }

  const { cameras, events, alerts, snapshots, recordings } = data;

  const statCards = [
    { label: 'Total Cameras', value: cameras?.total, icon: Camera, color: '#3b82f6', bg: '#eff6ff' },
    { label: 'Active', value: cameras?.active, icon: Eye, color: '#10b981', bg: '#ecfdf5' },
    { label: 'Inactive', value: cameras?.inactive, icon: Camera, color: '#ef4444', bg: '#fef2f2' },
    { label: 'Recording', value: cameras?.recording, icon: Video, color: '#8b5cf6', bg: '#f3e8ff' },
    { label: 'Events Today', value: events?.today, icon: Activity, color: '#f59e0b', bg: '#fffbeb' },
    { label: 'Alerts Unread', value: alerts?.unread, icon: AlertTriangle, color: '#f97316', bg: '#fff7ed' },
    { label: 'Snapshots Today', value: snapshots?.today, icon: Image, color: '#06b6d4', bg: '#ecfeff' },
    { label: 'Storage Used', value: `${recordings?.total_size_mb ?? 0} MB`, icon: HardDrive, color: '#64748b', bg: '#f8fafc' },
  ];

  const hourlyChart = {
    labels: (events?.hourly_trend ?? []).map(h => `${String(h.hour).padStart(2, '0')}h`),
    datasets: [{
      label: 'Events',
      data: (events?.hourly_trend ?? []).map(h => h.count),
      backgroundColor: 'rgba(251,191,36,0.75)',
      borderColor: '#d97706',
      borderWidth: 1,
    }],
  };

  const TYPE_COLORS = ['#3b82f6', '#a855f7', '#f97316', '#10b981', '#ef4444'];
  const typeTotal = (events?.by_type ?? []).reduce((s, t) => s + t.count, 0) || 1;

  return (
    <div className="responsive-box">
      <div style={{ maxWidth: '1700px', margin: '0 auto' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '28px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: '700', margin: '0 0 4px 0', color: '#0f172a' }}>📹 Monitor Dashboard</h1>
            <p style={{ color: '#64748b', fontSize: '14px', margin: 0 }}>Surveillance system overview</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '28px' }}>
          {statCards.map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} style={card}>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>
                <Icon size={20} color={color} />
              </div>
              <div style={{ fontSize: '28px', fontWeight: '700', color: '#111827' }}>{value ?? '—'}</div>
              <div style={{ fontSize: '13px', color: '#6b7280', fontWeight: '600' }}>{label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px', marginBottom: '24px' }}>
          <div style={{ ...card, height: '300px', display: 'flex', flexDirection: 'column' }}>
            <BarChart labels={hourlyChart.labels} datasets={hourlyChart.datasets} title="Hourly Event Trend" height="100%" noMargin noBorder noShadow />
          </div>
          <div style={{ ...card, display: 'flex', flexDirection: 'column' }}>
            {(events?.by_type ?? []).length > 0 && (
              <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Tag size={16} color="#6366f1" /> Events by Object Type
              </h2>
            )}
            {(events?.by_type ?? []).length === 0 ? (
              <NoData />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {(events.by_type).map((t, i) => {
                  const color = TYPE_COLORS[i % TYPE_COLORS.length];
                  const bg = [`#eff6ff`, `#f3e8ff`, `#fff7ed`, `#ecfdf5`, `#fef2f2`][i % 5];
                  return (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 16px', background: bg, borderRadius: '10px', border: `1px solid ${color}25` }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Tag size={18} color={color} />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#334155', textTransform: 'capitalize' }}>{t.object_type}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '1px' }}>{Math.round((t.count / typeTotal) * 100)}% of total</div>
                      </div>
                      <div style={{ fontSize: '24px', fontWeight: '800', color }}>{t.count}</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '20px' }}>
          <div style={card}>
            {(events?.by_severity ?? []).length > 0 && (
              <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ShieldAlert size={16} color="#ef4444" /> Events by Severity
              </h2>
            )}
            {(events?.by_severity ?? []).length === 0 ? <NoData /> : (
              <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                {events.by_severity.map((s, i) => (
                  <div key={i} style={{ flex: 1, textAlign: 'center', padding: '16px 8px', background: `${SEVERITY_COLOR[s.severity]}12`, border: `1.5px solid ${SEVERITY_COLOR[s.severity]}30`, borderRadius: '10px' }}>
                    <div style={{ fontSize: '26px', fontWeight: '800', color: SEVERITY_COLOR[s.severity] }}>{s.count}</div>
                    <div style={{ fontSize: '11px', color: SEVERITY_COLOR[s.severity], fontWeight: '700', textTransform: 'uppercase', marginTop: '4px', letterSpacing: '0.08em' }}>{s.severity}</div>
                  </div>
                ))}
              </div>
            )}

            {(events?.by_camera ?? []).length > 0 && (
              <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', marginBottom: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Camera size={16} color="#3b82f6" /> Events by Camera
              </h2>
            )}
            {(events?.by_camera ?? []).length === 0 ? <NoData /> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {events.by_camera.map((c, i) => {
                  const max = Math.max(...events.by_camera.map(x => x.count), 1);
                  return (
                    <div key={i}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: '#334155' }}>{c.camera__name}</span>
                        <span style={{ fontSize: '12px', fontWeight: '700', color: '#FFAF32' }}>{c.count}</span>
                      </div>
                      <div style={{ height: '6px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${(c.count / max) * 100}%`, background: 'linear-gradient(90deg,#fbbf24,#d97706)', borderRadius: '4px' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <div style={card}>
            {(alerts?.recent ?? []).length > 0 && (
              <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Bell size={16} color="#f97316" /> Recent Alerts
              </h2>
            )}
            {alerts?.recent?.length === 0 ? <NoData /> : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {(alerts?.recent ?? []).map((a, i) => (
                  <div key={a.id ?? i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#f97316', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{a.message}</div>
                      <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{a.camera__name}</div>
                    </div>
                    <div style={{ fontSize: '10px', color: '#cbd5e1', flexShrink: 0, fontFamily: 'monospace' }}>
                      {a.fired_at ? new Date(a.fired_at).toLocaleTimeString('en-GB', { hour12: false }) : '—'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={card}>
            {(snapshots?.recent ?? []).length > 0 && (
              <h2 style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Image size={16} color="#06b6d4" /> Recent Snapshots
              </h2>
            )}
            {snapshots?.recent?.length === 0 ? <NoData /> : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '10px' }}>
                {(snapshots?.recent ?? []).map((s, i) => {
                  const rel = s.url || s.image_url || s.snapshot_url || '';
                  const imgUrl = rel.startsWith('http') ? rel : rel.startsWith('/') ? `${getHost()}${rel}` : `${getHost()}/${rel}`;
                  return (
                  <div key={s.id ?? i} style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                    <img src={imgUrl} alt={s.camera__name} style={{ width: '100%', height: '70px', objectFit: 'cover', display: 'block' }} onError={e => { e.target.style.display = 'none'; }} />
                    <div style={{ padding: '5px 7px', background: '#f8fafc' }}>
                      <div style={{ fontSize: '10px', fontWeight: '700', color: '#1e293b', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.camera__name}</div>
                      <div style={{ fontSize: '9px', color: '#94a3b8', fontFamily: 'monospace' }}>
                        {s.taken_at ? new Date(s.taken_at).toLocaleTimeString('en-GB', { hour12: false }) : '—'}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>

      <ToastContainer />
    </div>
  );
}
